const ROOT_SELECTOR = ".js-show-more";
const CONTENT_SELECTOR = ".js-show-more-content";
const TOGGLE_SELECTOR = ".js-show-more-toggle";
const DEFAULT_COLLAPSE_HEIGHT = 850;
const DEFAULT_SHOW_MORE_TEXT = "mehr anzeigen";
const DEFAULT_SHOW_LESS_TEXT = "weniger anzeigen";
/*
Usage:
  <div class="js-show-more"
       data-show-more-height="850"
       data-show-more-text="mehr anzeigen"
       data-show-less-text="weniger anzeigen">
    <div class="js-show-more-content">...</div>
  </div>

Alle data-Attribute sind optional.
Fallbacks: DEFAULT_COLLAPSE_HEIGHT, DEFAULT_SHOW_MORE_TEXT, DEFAULT_SHOW_LESS_TEXT.
*/

let isInitialized = false;

let resizeRafId = null;
const TRANSPARENT_RGBA = "rgba(0, 0, 0, 0)";

function collectRootElements(rootNode)
{
    if (!rootNode || typeof rootNode.querySelectorAll !== "function")
    {
        return [];
    }

    const rootElements = Array.from(rootNode.querySelectorAll(ROOT_SELECTOR));

    if (typeof rootNode.matches === "function" && rootNode.matches(ROOT_SELECTOR))
    {
        rootElements.unshift(rootNode);
    }

    return rootElements;
}

function parseCollapseHeight(value)
{
    const parsedValue = Number.parseInt(value, 10);

    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : DEFAULT_COLLAPSE_HEIGHT;
}

function getContentElement(rootElement)
{
    return rootElement.querySelector(CONTENT_SELECTOR);
}

function getShowMoreText(rootElement)
{
    return rootElement.getAttribute("data-show-more-text") || DEFAULT_SHOW_MORE_TEXT;
}

function getShowLessText(rootElement)
{
    return rootElement.getAttribute("data-show-less-text") || DEFAULT_SHOW_LESS_TEXT;
}

function getCollapseHeight(rootElement)
{
    return parseCollapseHeight(rootElement.getAttribute("data-show-more-height"));
}

function isTransparentColor(colorValue)
{
    return !colorValue || colorValue === "transparent" || colorValue === TRANSPARENT_RGBA;
}

function resolveFadeColor(rootElement)
{
    if (typeof window === "undefined")
    {
        return "";
    }

    const parentElement = rootElement.parentElement;
    const parentBackgroundColor = parentElement ? window.getComputedStyle(parentElement).backgroundColor : "";

    if (!isTransparentColor(parentBackgroundColor))
    {
        return parentBackgroundColor;
    }

    const rootBackgroundColor = window.getComputedStyle(rootElement).backgroundColor;

    if (!isTransparentColor(rootBackgroundColor))
    {
        return rootBackgroundColor;
    }

    return "";
}

function updateFadeBackground(rootElement)
{
    const fadeColor = resolveFadeColor(rootElement);

    if (!fadeColor)
    {
        rootElement.style.removeProperty("--show-more-parent-bg");
        return;
    }

    rootElement.style.setProperty("--show-more-parent-bg", fadeColor);
}

function updateToggleLabel(rootElement, expanded)
{
    const toggleButton = rootElement.querySelector(TOGGLE_SELECTOR);

    if (!toggleButton)
    {
        return;
    }

    toggleButton.setAttribute("aria-expanded", expanded ? "true" : "false");
}

function ensureToggleButton(rootElement)
{
    const existingButton = rootElement.querySelector(TOGGLE_SELECTOR);

    if (existingButton)
    {
        return existingButton;
    }

    const toggleButton = document.createElement("button");

    toggleButton.type = "button";
    toggleButton.className = "btn btn-link btn-inline-block text-primary text-decoration-none py-2 pl-0 js-show-more-toggle";
    toggleButton.setAttribute("aria-expanded", "false");

    const showMoreSpan = document.createElement("span");

    showMoreSpan.className = "font-weight-bold show-more";
    showMoreSpan.appendChild(document.createTextNode(`${getShowMoreText(rootElement)} `));
    const showMoreIcon = document.createElement("i");

    showMoreIcon.className = "fa fa-chevron-down float-none text-dark small";
    showMoreSpan.appendChild(showMoreIcon);

    const showLessSpan = document.createElement("span");

    showLessSpan.className = "font-weight-bold show-less";
    showLessSpan.appendChild(document.createTextNode(`${getShowLessText(rootElement)} `));
    const showLessIcon = document.createElement("i");

    showLessIcon.className = "fa fa-chevron-up float-none text-dark small";
    showLessSpan.appendChild(showLessIcon);

    toggleButton.appendChild(showMoreSpan);
    toggleButton.appendChild(showLessSpan);

    rootElement.appendChild(toggleButton);
    return toggleButton;
}

function setCollapsedState(rootElement, collapsedHeight)
{
    const contentElement = getContentElement(rootElement);

    if (!contentElement)
    {
        return;
    }

    rootElement.classList.add("is-collapsed");
    rootElement.classList.remove("is-expanded");
    contentElement.style.maxHeight = `${collapsedHeight}px`;
    updateToggleLabel(rootElement, false);
}

function setExpandedState(rootElement)
{
    const contentElement = getContentElement(rootElement);

    if (!contentElement)
    {
        return;
    }

    rootElement.classList.add("is-expanded");
    rootElement.classList.remove("is-collapsed");
    contentElement.style.maxHeight = "none";
    updateToggleLabel(rootElement, true);
}

function clearShowMoreState(rootElement)
{
    const contentElement = getContentElement(rootElement);
    const toggleButton = rootElement.querySelector(TOGGLE_SELECTOR);

    rootElement.classList.remove("is-collapsed", "is-expanded");

    if (contentElement)
    {
        contentElement.style.maxHeight = "none";
    }

    if (toggleButton)
    {
        toggleButton.style.display = "none";
    }
}

function updateShowMoreBlock(rootElement)
{
    const contentElement = getContentElement(rootElement);

    if (!contentElement)
    {
        return;
    }

    updateFadeBackground(rootElement);

    const collapsedHeight = getCollapseHeight(rootElement);
    const wasExpanded = rootElement.classList.contains("is-expanded");
    const toggleButton = ensureToggleButton(rootElement);

    contentElement.style.maxHeight = "none";
    const contentHeight = Math.ceil(contentElement.scrollHeight);
    const shouldCollapse = contentHeight > collapsedHeight + 1;

    if (!shouldCollapse)
    {
        clearShowMoreState(rootElement);
        return;
    }

    toggleButton.style.display = "inline-block";

    if (wasExpanded)
    {
        setExpandedState(rootElement);
    }
    else
    {
        setCollapsedState(rootElement, collapsedHeight);
    }
}

function handleToggleClick(event)
{
    const toggleButton = event.target.closest(TOGGLE_SELECTOR);

    if (!toggleButton)
    {
        return;
    }

    const rootElement = toggleButton.closest(ROOT_SELECTOR);

    if (!rootElement)
    {
        return;
    }

    event.preventDefault();

    if (rootElement.classList.contains("is-expanded"))
    {
        setCollapsedState(rootElement, getCollapseHeight(rootElement));
    }
    else
    {
        setExpandedState(rootElement);
    }
}

function handleResize()
{
    if (resizeRafId || typeof window === "undefined")
    {
        return;
    }

    resizeRafId = window.requestAnimationFrame(() =>
    {
        resizeRafId = null;
        refreshShowMoreBlocks();
    });
}

function handleMediaLoad(event)
{
    const targetElement = event.target;

    if (!targetElement || typeof targetElement.closest !== "function")
    {
        return;
    }

    const contentElement = targetElement.closest(CONTENT_SELECTOR);

    if (!contentElement)
    {
        return;
    }

    const rootElement = contentElement.closest(ROOT_SELECTOR);

    if (!rootElement)
    {
        return;
    }

    updateShowMoreBlock(rootElement);
}

export function refreshShowMoreBlocks(rootNode = null)
{
    if (typeof document === "undefined")
    {
        return;
    }

    const effectiveRootNode = rootNode || document;

    if (!effectiveRootNode)
    {
        return;
    }
    const rootElements = collectRootElements(effectiveRootNode);

    rootElements.forEach((rootElement) =>
    {
        updateShowMoreBlock(rootElement);
    });
}

export function initShowMoreBlocks(rootNode = null)
{
    if (typeof window === "undefined" || typeof document === "undefined")
    {
        return;
    }

    if (!isInitialized)
    {
        document.addEventListener("click", handleToggleClick);
        document.addEventListener("load", handleMediaLoad, true);
        window.addEventListener("resize", handleResize);
        window.addEventListener("load", () => refreshShowMoreBlocks(), { once: true });
        isInitialized = true;
    }

    refreshShowMoreBlocks(rootNode);
}
